/*
 * USER MANAGEMENT PAGE — v1.10.8 (2026-05-09)
 *
 * Lists all users from MongoDB (read-only) merged with SQLite-native users
 * (users_app) + SQLite overrides on Mongo users (users_overrides).
 *
 * Permission model:
 *   - ANY authenticated user can READ.
 *   - ONLY users with roles[]∋'admin' can CREATE / UPDATE / DELETE.
 *   - Backend enforces this on the mutating endpoints (403 to non-admin).
 *   - The page renders write controls only when the caller is admin.
 *     Bootstrap: username 'Yanir' is hardcoded admin (matches dashboard.py).
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getUsers, createUser, updateUser, deleteUser, getAuthUser, getAuthUserObj,
         listAccessControl, grantAccess, updateAccess, revokeAccess } from '../services/api';

const ROLE_OPTIONS = ['admin', 'tech', 'site_manager', 'area_manager', 'warehouse_manager'];
const COUNTRY_OPTIONS = ['Israel', 'India'];

const ROLE_BADGE_STYLES = {
  admin: { bg: '#fde2e2', fg: '#c0392b' },
  tech: { bg: '#dbeafe', fg: '#1e40af' },
  site_manager: { bg: '#fff3cd', fg: '#856404' },
  area_manager: { bg: '#ede7ff', fg: '#6c5ce7' },
  warehouse_manager: { bg: '#d5f5ea', fg: '#0d6e3a' },
};

function RoleBadge({ role }) {
  const c = ROLE_BADGE_STYLES[role] || { bg: '#e4e8ee', fg: '#636e72' };
  return (
    <span style={{
      background: c.bg, color: c.fg,
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
      letterSpacing: 0.2, marginRight: 4, textTransform: 'capitalize',
    }}>
      {role.replace(/_/g, ' ')}
    </span>
  );
}

function UserEditModal({ user, isAdmin, isCreate, warehouseManagers, onClose, onSave }) {
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    roles: user?.roles || [],
    country: user?.country || '',
    region: user?.region || '',
    sites: user?.sites || [],
    home_warehouse_id: user?.home_warehouse_id || '',
    active: user?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const toggleRole = (r) => {
    setForm(f => ({
      ...f,
      roles: f.roles.includes(r) ? f.roles.filter(x => x !== r) : [...f.roles, r],
    }));
  };
  const sitesText = (form.sites || []).join(', ');
  const onSitesChange = (val) => {
    setForm(f => ({
      ...f,
      sites: val.trim() === '' ? [] : val.split(',').map(s => s.trim()).filter(Boolean),
    }));
  };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      const payload = {
        full_name: form.full_name, email: form.email, phone: form.phone,
        roles: form.roles, country: form.country || null,
        region: form.region || null, sites: form.sites,
        home_warehouse_id: form.home_warehouse_id || null,
        active: form.active,
      };
      if (isCreate) await onSave(payload, true);
      else await onSave(payload, false);
    } catch (e) { setError(e.message || String(e)); }
    finally { setSaving(false); }
  };

  return (
    <div style={mStyles.backdrop} onClick={onClose}>
      <div style={mStyles.modal} onClick={e => e.stopPropagation()}>
        <h3 style={{margin: 0, fontSize: 18}}>
          {isCreate ? 'New user' : `Edit ${user.full_name}`}
          {!isAdmin && <span style={{fontSize: 12, color: '#636e72', marginLeft: 8}}>(read-only — admin only)</span>}
        </h3>
        {!isCreate && (
          <div style={{fontSize: 11, color: '#636e72', marginTop: 2}}>
            <code>{user.id}</code> · <span style={{
              background: user.source === 'app' ? '#d5f5ea' : '#dbeafe',
              color: user.source === 'app' ? '#0d6e3a' : '#1e40af',
              padding: '1px 6px', borderRadius: 3, fontSize: 10, marginLeft: 4,
            }}>{user.source === 'app' ? 'app' : 'mongo'}</span>
          </div>
        )}
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16}}>
          <Field label="Full name" required>
            <input style={mStyles.input} value={form.full_name} disabled={!isAdmin}
                   onChange={e => setForm(f => ({...f, full_name: e.target.value}))} />
          </Field>
          <Field label="Country">
            <select style={mStyles.input} value={form.country || ''} disabled={!isAdmin}
                    onChange={e => setForm(f => ({...f, country: e.target.value}))}>
              <option value="">(none)</option>
              {COUNTRY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Email" required>
            <input style={mStyles.input} type="email" value={form.email} disabled={!isAdmin}
                   onChange={e => setForm(f => ({...f, email: e.target.value}))} />
          </Field>
          <Field label="Phone" required>
            <input style={mStyles.input} value={form.phone} disabled={!isAdmin}
                   placeholder="+972500000000"
                   onChange={e => setForm(f => ({...f, phone: e.target.value}))} />
          </Field>
          <Field label="Region (use 'ALL' for dynamic — auto-includes new regions)">
            <input style={mStyles.input} value={form.region || ''} disabled={!isAdmin}
                   placeholder="ALL"
                   onChange={e => setForm(f => ({...f, region: e.target.value}))} />
          </Field>
          <Field label="Home warehouse">
            <select style={mStyles.input} value={form.home_warehouse_id || ''} disabled={!isAdmin}
                    onChange={e => setForm(f => ({...f, home_warehouse_id: e.target.value || null}))}>
              <option value="">(none)</option>
              {warehouseManagers.map(w => (
                <option key={w.id} value={w.id}>{w.full_name} — {w.email}</option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Sites (comma-separated; use 'ALL' for dynamic — auto-includes new sites)">
          <input style={mStyles.input} value={sitesText} disabled={!isAdmin}
                 placeholder="ALL  or  site_id_1, site_id_2"
                 onChange={e => onSitesChange(e.target.value)} />
        </Field>
        <Field label="Roles (multi-select)">
          <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
            {ROLE_OPTIONS.map(r => {
              const selected = form.roles.includes(r);
              return (
                <button key={r} type="button"
                  disabled={!isAdmin}
                  onClick={() => toggleRole(r)}
                  style={{
                    padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                    cursor: isAdmin ? 'pointer' : 'default',
                    border: '1px solid ' + (selected ? '#475569' : '#cbd5e1'),
                    background: selected ? '#475569' : '#fff',
                    color: selected ? '#fff' : '#475569',
                    textTransform: 'capitalize',
                  }}>
                  {r.replace(/_/g, ' ')}
                </button>
              );
            })}
          </div>
        </Field>
        <Field label="Active">
          <label style={{display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13}}>
            <input type="checkbox" checked={form.active} disabled={!isAdmin}
                   onChange={e => setForm(f => ({...f, active: e.target.checked}))} />
            {form.active ? 'active' : 'deactivated'}
          </label>
        </Field>

        {error && <div style={{color: '#c0392b', marginTop: 12, fontSize: 12}}>{error}</div>}

        <div style={{display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end'}}>
          <button onClick={onClose} style={mStyles.btnSecondary} disabled={saving}>
            {isAdmin ? 'Cancel' : 'Close'}
          </button>
          {isAdmin && (
            <button onClick={handleSave} disabled={saving} style={mStyles.btnPrimary}>
              {saving ? 'Saving…' : (isCreate ? 'Create' : 'Save')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{marginBottom: 12}}>
      <div style={{fontSize: 11, color: '#636e72', textTransform: 'uppercase',
                   letterSpacing: 0.5, marginBottom: 4, fontWeight: 600}}>
        {label}{required && <span style={{color: '#c0392b'}}> *</span>}
      </div>
      {children}
    </div>
  );
}

// v1.10.42 — Access control management modal.
function AccessControlModal({ row, onClose, onSaved }) {
  const isNew = !!row._isNew;
  const [role, setRole] = React.useState(row.role || 'tech');
  const [expire, setExpire] = React.useState(row.expire_access_date || '2035-12-31');
  const [notes, setNotes] = React.useState(row.notes || '');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);
  const ROLES_ALL = ['admin','area_manager','country_admin','country_manager','warehouse','tech','viewer','operator'];

  const onSave = async () => {
    setSaving(true); setError(null);
    try {
      if (isNew) {
        await grantAccess({ phone_number: row.phone, role, full_name: row.full_name || null, email: row.email || null, expire_access_date: expire, notes: notes || null });
      } else {
        await updateAccess(row.phone_number, { role, expire_access_date: expire, notes: notes || null });
      }
      onSaved();
    } catch (e) { setError(e.message || String(e)); }
    finally { setSaving(false); }
  };
  const onRevoke = async () => {
    if (!window.confirm(`Revoke access for ${row.phone_number}? (sets expire to yesterday)`)) return;
    setSaving(true); setError(null);
    try { await revokeAccess(row.phone_number); onSaved(); }
    catch (e) { setError(e.message || String(e)); }
    finally { setSaving(false); }
  };

  const wrap = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const card = { background: '#fff', borderRadius: 8, padding: 24, width: 420, maxWidth: '90vw' };
  const label = { display: 'block', fontSize: 12, color: '#636e72', marginTop: 8, marginBottom: 4 };
  const input = { width: '100%', padding: '8px 10px', border: '1px solid #d0d6dd', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' };
  const btn = { padding: '8px 16px', background: '#6C5CE7', border: 'none', borderRadius: 4, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };

  return (
    <div style={wrap} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={card}>
        <h3 style={{margin: 0, marginBottom: 14, fontSize: 16}}>{isNew ? 'Grant access' : 'Edit access'} — {row.phone || row.phone_number}</h3>
        {error && <div style={{color: '#c0392b', fontSize: 12, marginBottom: 10}}>{error}</div>}
        {row.full_name && <div style={{color: '#636e72', fontSize: 12, marginBottom: 10}}>{row.full_name}{row.email ? ` · ${row.email}` : ''}</div>}
        <label style={label}>Role</label>
        <select style={input} value={role} onChange={e => setRole(e.target.value)}>
          {ROLES_ALL.map(r => <option key={r} value={r}>{r.replace(/_/g,' ')}</option>)}
        </select>
        <label style={label}>Expires</label>
        <input style={input} type="date" value={expire} onChange={e => setExpire(e.target.value)} />
        <label style={label}>Notes (optional)</label>
        <input style={input} type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. pilot, contractor" />
        <div style={{display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end'}}>
          {!isNew && (
            <button style={{ ...btn, background: '#c0392b' }} onClick={onRevoke} disabled={saving}>Revoke</button>
          )}
          <button style={{ ...btn, background: '#bdc3c7', color: '#2c3e50' }} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={btn} onClick={onSave} disabled={saving}>{saving ? 'Saving…' : (isNew ? 'Grant' : 'Save')}</button>
        </div>
      </div>
    </div>
  );
}

export default function Users() {
  // v1.10.42 — admin check via auth_user_obj.roles (replaces hardcoded 'Yanir').
  const authUser = getAuthUser();
  const authUserObj = getAuthUserObj();
  const isAdmin = !!(authUserObj && (authUserObj.roles || []).includes('admin'));

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState(null); // user object | 'new' | null
  // v1.10.42 — access_control state
  const [acRows, setAcRows] = useState([]);
  const [acFilter, setAcFilter] = useState('');
  const [acEditing, setAcEditing] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const opts = {};
      if (filterRole) opts.role = filterRole;
      if (filterCountry) opts.country = filterCountry;
      if (includeInactive) opts.includeInactive = true;
      const r = await getUsers(opts);
      setUsers(r.users || []);
      try {
        const ac = await listAccessControl();
        setAcRows(ac.rows || []);
      } catch (e) { /* 403/401 — leave acRows empty */ }
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }, [filterRole, filterCountry, includeInactive]);

  useEffect(() => { reload(); }, [reload]);

  const normalizePhone = (raw) => {
    if (!raw) return '';
    let p = String(raw).trim();
    let kept = '';
    for (const ch of p) { if (ch === '+' || (ch >= '0' && ch <= '9')) kept += ch; }
    if (!kept) return '';
    if (kept[0] === '0' && kept.length >= 9 && (kept[1] === '5' || kept[1] === '7')) {
      return '+972' + kept.slice(1);
    }
    if (kept[0] === '+') return '+' + kept.slice(1).replace(/\D/g, '');
    return kept;
  };
  const acByPhone = useMemo(() => {
    const m = new Map();
    for (const r of acRows) m.set(r.phone_number, r);
    return m;
  }, [acRows]);
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const plus30ISO = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); }, []);
  const getAc = (u) => acByPhone.get(normalizePhone(u.phone || ''));
  const acState = (ac) => {
    if (!ac) return 'no_access';
    if ((ac.expire_access_date || '') < todayISO) return 'expired';
    if ((ac.expire_access_date || '') <= plus30ISO) return 'expiring30';
    return 'active';
  };

  const warehouseManagers = useMemo(
    () => users.filter(u => (u.roles || []).includes('warehouse_manager')),
    [users]);

  const filtered = useMemo(() => {
    let list = users;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(u =>
        (u.full_name || '').toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q)
        || (u.phone || '').includes(q)
      );
    }
    if (acFilter) list = list.filter(u => acState(getAc(u)) === acFilter);
    return list;
  }, [users, search, acFilter, acByPhone, todayISO, plus30ISO]);

  const handleSave = async (payload, isCreate) => {
    if (isCreate) await createUser(payload);
    else await updateUser(editing.id, payload);
    setEditing(null);
    await reload();
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Deactivate ${u.full_name}? (soft delete — can reactivate)`)) return;
    try { await deleteUser(u.id); await reload(); }
    catch (e) { alert(e.message || String(e)); }
  };

  return (
    <div style={pageStyles.page}>
      <div style={pageStyles.header}>
        <h1 style={pageStyles.title}>Users</h1>
        {isAdmin && (
          <button style={pageStyles.btnPrimary} onClick={() => setEditing('new')}>+ New user</button>
        )}
      </div>
      {!isAdmin && (
        <div style={pageStyles.notice}>
          You're viewing in read-only mode. Edits require an <code>admin</code> role.
        </div>
      )}

      <div style={pageStyles.toolbar}>
        <input style={pageStyles.search} placeholder="Search name / email / phone…"
               value={search} onChange={e => setSearch(e.target.value)} />
        <select style={pageStyles.filter} value={filterRole} onChange={e => setFilterRole(e.target.value)}>
          <option value="">All roles</option>
          {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
        </select>
        <select style={pageStyles.filter} value={filterCountry} onChange={e => setFilterCountry(e.target.value)}>
          <option value="">All countries</option>
          {COUNTRY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select style={pageStyles.filter} value={acFilter} onChange={e => setAcFilter(e.target.value)}>
          <option value="">All access</option>
          <option value="active">Active</option>
          <option value="expiring30">Expiring in 30 days</option>
          <option value="expired">Expired</option>
          <option value="no_access">No access yet</option>
        </select>
        <label style={{display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#636e72'}}>
          <input type="checkbox" checked={includeInactive}
                 onChange={e => setIncludeInactive(e.target.checked)} />
          show deactivated
        </label>
        <span style={{marginLeft: 'auto', fontSize: 12, color: '#636e72'}}>
          {filtered.length} of {users.length} users
        </span>
      </div>

      {loading && <div style={pageStyles.loadingMsg}>Loading users…</div>}
      {error && <div style={pageStyles.error}>Error: {error}</div>}

      {!loading && (
        <div style={pageStyles.tableWrap}>
          <table style={pageStyles.table}>
            <thead>
              <tr>
                <th style={pageStyles.th}>Name</th>
                <th style={pageStyles.th}>Email</th>
                <th style={pageStyles.th}>Phone</th>
                <th style={pageStyles.th}>Roles</th>
                <th style={pageStyles.th}>Country</th>
                <th style={pageStyles.th}>Region</th>
                <th style={pageStyles.th}>Sites</th>
                <th style={pageStyles.th}>Source</th>
                <th style={pageStyles.th}>Access</th>
                <th style={pageStyles.th}>Expires</th>
                <th style={pageStyles.th}>Status</th>
                <th style={pageStyles.th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} style={{padding: 24, textAlign: 'center', color: '#999'}}>No users match.</td></tr>
              )}
              {filtered.map(u => (
                <tr key={u.id} style={{
                  borderBottom: '1px solid #f0f0f0',
                  background: u.active ? 'transparent' : '#f8f9fa',
                  opacity: u.active ? 1 : 0.6,
                }}>
                  <td style={pageStyles.td}><strong>{u.full_name}</strong></td>
                  <td style={pageStyles.td}>{u.email}</td>
                  <td style={pageStyles.td}><code style={{fontSize: 11}}>{u.phone}</code></td>
                  <td style={pageStyles.td}>
                    {(u.roles || []).length === 0
                      ? <span style={{color: '#b2bec3', fontSize: 11}}>—</span>
                      : (u.roles || []).map(r => <RoleBadge key={r} role={r} />)}
                  </td>
                  <td style={pageStyles.td}>{u.country || <span style={{color: '#b2bec3'}}>—</span>}</td>
                  <td style={pageStyles.td}>{u.region || <span style={{color: '#b2bec3'}}>—</span>}</td>
                  <td style={pageStyles.td} title={(u.sites || []).join(', ')}>
                    {(u.sites || []).length === 0
                      ? <span style={{color: '#b2bec3'}}>—</span>
                      : (u.sites || []).includes('ALL')
                        ? <span style={{fontSize: 11, fontWeight: 700, color: '#0d6e3a'}}>ALL</span>
                        : <span style={{fontSize: 11}}>{(u.sites || []).length} sites</span>}
                  </td>
                  <td style={pageStyles.td}>
                    <span style={{
                      background: u.source === 'app' ? '#d5f5ea' : '#dbeafe',
                      color: u.source === 'app' ? '#0d6e3a' : '#1e40af',
                      padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: 0.5,
                    }}>{u.source}</span>
                  </td>
                  <td style={pageStyles.td}>
                    {(() => {
                      const ac = getAc(u);
                      if (!ac) {
                        if (!isAdmin) return <span style={{color: '#bbb'}}>—</span>;
                        return (
                          <button
                            style={{ padding: '3px 10px', background: '#0d6e3a', border: 'none', borderRadius: 4, color: '#fff', fontSize: 11, fontWeight: 500, cursor: 'pointer' }}
                            onClick={() => setAcEditing({ phone: normalizePhone(u.phone || ''), full_name: u.full_name, email: u.email, role: 'tech', expire_access_date: '2035-12-31', _isNew: true })}
                          >+ Grant</button>
                        );
                      }
                      const st = acState(ac);
                      const bg = st === 'active' ? '#d5f5ea' : st === 'expiring30' ? '#fff3cd' : '#fde2e2';
                      const fg = st === 'active' ? '#0d6e3a' : st === 'expiring30' ? '#856404' : '#c0392b';
                      return (
                        <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                          <span style={{background: bg, color: fg, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600}}>
                            {ac.role.replace(/_/g, ' ')}
                          </span>
                          {isAdmin && (
                            <button style={{ background: 'transparent', border: 'none', color: '#6C5CE7', fontSize: 11, cursor: 'pointer', textDecoration: 'underline', padding: 0 }} onClick={() => setAcEditing(ac)}>edit</button>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={pageStyles.td}>
                    {(() => {
                      const ac = getAc(u);
                      if (!ac) return <span style={{color: '#bbb'}}>—</span>;
                      const st = acState(ac);
                      const color = st === 'expired' ? '#c0392b' : st === 'expiring30' ? '#856404' : '#636e72';
                      return <span style={{color, fontSize: 12}}>{ac.expire_access_date}</span>;
                    })()}
                  </td>
                  <td style={pageStyles.td}>
                    {u.active
                      ? <span style={{color: '#0d6e3a', fontSize: 11, fontWeight: 600}}>● active</span>
                      : <span style={{color: '#999', fontSize: 11}}>○ inactive</span>}
                  </td>
                  <td style={pageStyles.td}>
                    <button style={pageStyles.btnRow} onClick={() => setEditing(u)}>
                      {isAdmin ? 'Edit' : 'View'}
                    </button>
                    {isAdmin && u.active && (
                      <button style={{...pageStyles.btnRow, marginLeft: 4, color: '#c0392b'}}
                              onClick={() => handleDelete(u)}>Deactivate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {acEditing && (
        <AccessControlModal
          row={acEditing}
          onClose={() => setAcEditing(null)}
          onSaved={async () => { setAcEditing(null); await reload(); }}
        />
      )}

      {editing && editing !== 'new' && (
        <UserEditModal user={editing} isAdmin={isAdmin} isCreate={false}
                        warehouseManagers={warehouseManagers}
                        onClose={() => setEditing(null)} onSave={handleSave} />
      )}
      {editing === 'new' && (
        <UserEditModal user={null} isAdmin={isAdmin} isCreate={true}
                        warehouseManagers={warehouseManagers}
                        onClose={() => setEditing(null)} onSave={handleSave} />
      )}
    </div>
  );
}

const pageStyles = {
  page: { padding: '24px 32px', maxWidth: 1400, fontFamily: "'Inter', -apple-system, sans-serif" },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  title: { fontSize: 26, fontWeight: 700, color: '#2d3436', margin: 0 },
  notice: { background: '#fff3cd', border: '1px solid #ffe5b3', padding: '8px 14px',
              borderRadius: 6, fontSize: 13, color: '#856404', marginBottom: 16 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 },
  search: { padding: '8px 12px', borderRadius: 4, border: '1px solid #cbd5e1',
             fontSize: 13, width: 300, fontFamily: 'inherit' },
  filter: { padding: '8px 10px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 13, fontFamily: 'inherit' },
  btnPrimary: { background: '#475569', color: '#fff', border: 'none',
                 padding: '8px 16px', borderRadius: 4, cursor: 'pointer',
                 fontWeight: 600, fontSize: 13, fontFamily: 'inherit' },
  loadingMsg: { padding: 32, textAlign: 'center', color: '#636e72' },
  error: { padding: 16, background: '#fde2e2', color: '#c0392b', borderRadius: 4 },
  tableWrap: { background: '#fff', border: '1px solid #e4e8ee', borderRadius: 6, overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', background: '#fafbfc',
        borderBottom: '1px solid #e4e8ee', color: '#636e72', fontWeight: 600,
        fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 },
  td: { padding: '8px 12px', verticalAlign: 'middle' },
  btnRow: { background: 'none', border: '1px solid #cbd5e1', padding: '4px 10px',
              borderRadius: 3, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' },
};

const mStyles = {
  backdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.4)', zIndex: 1000,
                display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#fff', borderRadius: 8, padding: 24, width: 720, maxWidth: '90vw',
             maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' },
  input: { width: '100%', padding: '8px 10px', borderRadius: 4, border: '1px solid #cbd5e1',
             fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' },
  btnPrimary: { background: '#475569', color: '#fff', border: 'none',
                  padding: '8px 18px', borderRadius: 4, cursor: 'pointer',
                  fontWeight: 600, fontSize: 13, fontFamily: 'inherit' },
  btnSecondary: { background: '#fff', color: '#475569', border: '1px solid #cbd5e1',
                    padding: '8px 18px', borderRadius: 4, cursor: 'pointer',
                    fontWeight: 600, fontSize: 13, fontFamily: 'inherit' },
};
